import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { BlockVisualization } from 'webapp/enums/enums';

import themed from 'webapp/decorators/themed';
import didUpdate from 'webapp/modifiers/did-update';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import ToolTipComponent from 'webapp/components/tool-tip';

export interface Args {
	blockType: BlockVisualization;
	onChangeBlockType: (blockType: BlockVisualization) => void;
	restrictedBlockTypes?: BlockVisualization[];
	isAttributed?: boolean;
	theme?: string;
}

@themed()
export default class BlocktypePickerComponent extends Component<Args> {
	@service('constants') declare constantsService: Service.Constants;

	declare themeClass: string; // from @themed

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		next(this, this.checkBlockType);
	}

	get allowedBlockTypes() {
		let types = [];
		if (this.args.restrictedBlockTypes) {
			types = this.args.restrictedBlockTypes;
		} else if (this.args.isAttributed) {
			types = [
				BlockVisualization.BAR_CHART,
				BlockVisualization.LINE_CHART,
				BlockVisualization.TABLE,
				BlockVisualization.PIE,
			];
		} else {
			types = [
				BlockVisualization.BAR_CHART,
				BlockVisualization.LINE_CHART,
				BlockVisualization.NUMBER,
			];
		}
		return types;
	}

	@cached
	get blockTypesOptions() {
		const types = this.allowedBlockTypes.map((id) => {
			return {
				id,
				name: this.constantsService.blockTypes.find((b) => b.value === id)?.label2,
				selected: this.args.blockType === id,
				iconName: this.getIconName(id),
			};
		});

		return types;
	}

	@action
	checkBlockType() {
		const selected = this.blockTypesOptions.find((item) => item.id === this.args.blockType);
		if (!selected) {
			const firstBlockType = this.blockTypesOptions[0]?.id;
			if (firstBlockType) {
				this.changeBlockType(firstBlockType);
			}
		}
	}

	@action
	changeBlockType(type: BlockVisualization) {
		this.args.onChangeBlockType(type);
	}

	private getIconName(type: BlockVisualization): IconName {
		switch (type) {
			case BlockVisualization.BAR_CHART:
				return 'ChartBar';
			case BlockVisualization.LINE_CHART:
				return 'ChartLine';
			case BlockVisualization.TABLE:
				return 'Table';
			case BlockVisualization.PIE:
				return 'ChartPie';
			case BlockVisualization.NUMBER:
				return 'Metrics';
			default:
				return 'ChartBar';
		}
	}
	<template>
		<div
			class="blocktype-picker {{this.themeClass}}"
			{{didUpdate this.checkBlockType @blockType @restrictedBlockTypes @isAttributed}}
		>
			{{#each this.blockTypesOptions as |option|}}
				<a
					role="button"
					{{on "click" (fn this.changeBlockType option.id)}}
					class="block{{option.id}} {{if option.selected 'selected'}}"
				>
					<CommonUiIconComponent @name={{option.iconName}} @size="lg" />
					<ToolTipComponent @text={{option.name}} />
				</a>
			{{/each}}
		</div>
	</template>
}
